import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Popconfirm,
  message,
  Alert,
  Tooltip,
  Switch,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useQuotations, useDeleteQuotation, useConvertQuotationToSalesOrder, useAcceptQuotation, QUOTATION_KEYS } from '../../hooks/useQuotation';
import { canDeleteQuotation, canEditQuotation } from '../../utils/documentDeletePolicy';
import { displayQuotationRevisionLabel } from '../../utils/quotationRevisionPolicy';
import { useExcelMigrationIo } from '@/hooks/useExcelMigrationIo';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import { Quotation } from '../../services/quotation.service';
import quotationService from '../../services/quotation.service';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AED_CURRENCY_CODE } from '@/constants/uaDirhamDisplay';
import { AedDirhamSign } from '@/components/currency/AedDirhamSign';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { resolveQuotationOwnerLabel } from '@/utils/quotationOwnerDisplay';
import { getReportDocumentFile } from '@/services/reportDocuments.service';
import { openBlobInNewTab } from '@/reports/utils/exportTable';
import { formatPercent } from '@/utils/format';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const { Title, Text } = Typography;
const { Option } = Select;

const QuotationsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const opportunityIdFilter = searchParams.get('opportunity_id') || undefined;
  const { baseCurrency, convertToBaseCurrency, loading: currencyLoading } = useCurrency();
  const { resolve: resolveUserDisplay } = useUserDisplayResolver();
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [teamFilter, setTeamFilter] = useState(() => searchParams.get('sales_team_id') || '');
  const [commercialStageFilter, setCommercialStageFilter] = useState(
    () => searchParams.get('commercial_stage') || ''
  );
  const [showAllRevisions, setShowAllRevisions] = useState(
    () => searchParams.get('all_revisions') === '1'
  );
  const [currentOfferOnly, setCurrentOfferOnly] = useState(false);
  const [expandedFamilies, setExpandedFamilies] = useState<Record<string, Quotation[]>>({});

  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });

  const revisionListFilter = showAllRevisions ? {} : { is_latest_revision: true as const };

  const {
    data: quotationsData,
    isLoading
  } = useQuotations({
    status: statusFilter && statusFilter !== 'AcceptedCurrent' ? statusFilter : undefined,
    opportunity_id: opportunityIdFilter,
    sales_team_id: teamFilter || undefined,
    search: debouncedSearch || undefined,
    commercial_stage: commercialStageFilter || undefined,
    ...revisionListFilter,
  });

  // Summary cards stay on status/team totals (ignore text search), matching prior UX
  const { data: quotationsStatsData } = useQuotations({
    status: statusFilter && statusFilter !== 'AcceptedCurrent' ? statusFilter : undefined,
    opportunity_id: opportunityIdFilter,
    sales_team_id: teamFilter || undefined,
    commercial_stage: commercialStageFilter || undefined,
    ...revisionListFilter,
  });
  
  const deleteQuotationMutation = useDeleteQuotation();
  const convertToSalesOrderMutation = useConvertQuotationToSalesOrder();
  const acceptQuotationMutation = useAcceptQuotation();

  const { isExcelBusy, downloadExport } = useExcelMigrationIo({
    templateUrl: '/crm/quotations/excel/template',
    exportUrl: '/crm/quotations/excel/export',
    importUrl: '/crm/quotations/excel/import',
    templateFilename: 'quotations-import-template.xlsx',
    exportFilenamePrefix: 'quotations',
    onImportSuccess: () => queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() }),
    buildExportParams: () => ({
      status: statusFilter || undefined,
      opportunity_id: opportunityIdFilter,
      sales_team_id: teamFilter || undefined,
      search: debouncedSearch || undefined,
    }),
  });

  const quotations: Quotation[] = Array.isArray(quotationsData) 
    ? quotationsData 
    : ((quotationsData as any)?.data || []);

  const visibleQuotations = useMemo(() => {
    let rows = quotations;
    if (currentOfferOnly) {
      rows = rows.filter((q) => q.is_current_submitted);
    }
    if (statusFilter === 'AcceptedCurrent') {
      rows = rows.filter((q) => q.status === 'Accepted' && q.is_current_submitted);
    }
    return rows;
  }, [quotations, currentOfferOnly, statusFilter]);

  const quotationsForStats: Quotation[] = Array.isArray(quotationsStatsData)
    ? quotationsStatsData
    : ((quotationsStatsData as any)?.data || []);

  // Helper function to safely convert to number
  const toNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Calculate statistics (unfiltered by search text)
  const totalQuotations = quotationsForStats.length;
  const totalValue = quotationsForStats.reduce(
    (sum, quot) => sum + convertToBaseCurrency(toNumber(quot.grand_total), quot.currency),
    0
  );
  const draftQuotations = quotationsForStats.filter(q => q.status === 'Draft').length;

  const handleView = (quotationId: string) => {
    navigate(`/growth/quotations/${quotationId}`);
  };

  const handleDelete = async (quotation: Quotation) => {
    try {
      await deleteQuotationMutation.mutateAsync(quotation.id);
      message.success(`Quotation ${quotation.quotation_number} deleted successfully`);
    } catch (error: any) {
      console.error('Error deleting quotation:', error);
      message.error(error.response?.data?.message || 'Failed to delete quotation');
    }
  };

  const handleAcceptQuotation = async (quotation: Quotation) => {
    try {
      await acceptQuotationMutation.mutateAsync({ id: quotation.id });
      message.success(`Quotation ${quotation.quotation_number} marked as Accepted`);
    } catch (error: any) {
      console.error('Error accepting quotation:', error);
      message.error(error.response?.data?.message || 'Failed to accept quotation');
    }
  };

  const handleConvertToSalesOrder = async (quotation: Quotation) => {
    try {
      // Check quotation status before attempting conversion
      if (quotation.status !== 'Accepted') {
        message.error(`Cannot convert quotation. Status must be "Accepted", current status: "${quotation.status}"`);
        return;
      }
      
      if (quotation.is_converted_to_sales_order) {
        message.warning('This quotation has already been converted to a customer order');
        return;
      }

      const result = await convertToSalesOrderMutation.mutateAsync(quotation.id);
      message.success(`Quotation ${quotation.quotation_number} converted to Customer Order ${result.sales_order_id ? `#${result.sales_order_id}` : ''} successfully`);
      // Navigate to sales orders list page to see the new order
      // Small delay to ensure backend has processed the transaction
      setTimeout(() => {
        navigate('/growth/orders');
      }, 500);
    } catch (error: any) {
      console.error('Error converting quotation:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to convert quotation to customer order';
      message.error(errorMessage);
    }
  };

  const handlePrint = async (quotation: Quotation) => {
    const hide = message.loading('Preparing quotation PDF...', 0);
    try {
      openBlobInNewTab(await getReportDocumentFile('sales-quotation', 'pdf', { id: quotation.id }));
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Could not generate the quotation PDF');
    } finally {
      hide();
    }
  };

  // Table columns
  const columns: ColumnsType<Quotation> = [
    {
      title: 'Quotation Number',
      dataIndex: 'quotation_number',
      key: 'quotation_number',
      fixed: 'left',
      width: 150,
      filteredValue: undefined,
      render: (text: string, record: Quotation) => (
        <Button
          type="link"
          onClick={() => handleView(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
          {!record.is_latest_revision ? (
            <Tag style={{ marginLeft: 4 }}>{displayQuotationRevisionLabel(record.revision_number)}</Tag>
          ) : null}
        </Button>
      )
    },
    {
      title: 'Revision',
      dataIndex: 'revision_number',
      key: 'revision_number',
      width: 90,
      render: (n: number, record: Quotation) => (
        <Space size={4}>
          <Tag>{displayQuotationRevisionLabel(n)}</Tag>
          {record.is_current_submitted ? <Tag color="blue">Current</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Customer Offer',
      key: 'customer_offer',
      width: 100,
      render: (_: unknown, record: Quotation) =>
        record.is_current_submitted ? <Tag color="blue">Current</Tag> : record.status === 'Revised' ? <Tag>Superseded</Tag> : '—',
    },
    {
      title: 'Margin %',
      key: 'margin_pct',
      width: 90,
      align: 'right' as const,
      render: (_: unknown, record: Quotation) => {
        const pct = record.profitability_snapshot?.gross_profit_percentage;
        return pct != null
          ? formatPercent(Number(pct), { semantics: 'whole', maximumFractionDigits: 2 })
          : '—';
      },
    },
    {
      title: 'Quotation Name',
      dataIndex: 'quotation_name',
      key: 'quotation_name',
      width: 200,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 180,
    },
    {
      title: 'Owner',
      dataIndex: 'sales_person',
      key: 'sales_person',
      width: 120,
      render: (_v: string, record: Quotation) =>
        resolveQuotationOwnerLabel(record, (id) => resolveUserDisplay(id, 'Unknown user')),
    },
    {
      title: 'Sales team',
      dataIndex: 'sales_team_name',
      key: 'sales_team_name',
      width: 160,
      render: (v: string) => v || '—',
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 220,
      filteredValue: undefined,
      render: (_: string, record: Quotation) => {
        const code = (record.project_number || record.project_summary?.project_number || '').trim();
        const name = (record.project_name || record.project_summary?.project_name || '').trim();
        if (code && name) return `${code} - ${name}`;
        return code || name || '—';
      },
    },
    {
      title: 'Date',
      dataIndex: 'quotation_date',
      key: 'quotation_date',
      width: 120,
      filteredValue: undefined,
      render: (date: string) => dayjs(date).format('DD MMM YYYY')
    },
    {
      title: 'Valid Until',
      dataIndex: 'valid_until',
      key: 'valid_until',
      width: 120,
      filteredValue: undefined,
      render: (date: string) => date ? dayjs(date).format('DD MMM YYYY') : '-'
    },
    {
      title: 'Grand Total',
      dataIndex: 'grand_total',
      key: 'grand_total',
      width: 150,
      align: 'right',
      filteredValue: undefined,
      render: (amount: number, record: Quotation) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.currency} {amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
          {record.vat_applicable && (
            <div style={{ fontSize: '12px', color: '#888' }}>
              incl. VAT {record.vat_percentage}%
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filteredValue: undefined,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          Draft: 'default',
          Sent: 'processing',
          Accepted: 'success',
          Rejected: 'error',
          Revised: 'warning'
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
      filters: [
        { text: 'Draft', value: 'Draft' },
        { text: 'Sent', value: 'Sent' },
        { text: 'Accepted', value: 'Accepted' },
        { text: 'Rejected', value: 'Rejected' }
      ],
      onFilter: (value: any, record: Quotation) => record.status === value
    },
    {
      title: 'Commercial outcome',
      dataIndex: 'commercial_stage',
      key: 'commercial_stage',
      width: 160,
      render: (_: string | undefined, record: Quotation) => {
        const stage = record.commercial_stage;
        const label = record.commercial_stage_label || '—';
        if (stage === 'won_pr_awaiting_so') return <Tag color="success">{label}</Tag>;
        if (stage === 'awaiting_customer_order') return <Tag color="warning">{label}</Tag>;
        if (stage === 'converted_to_so') return <Tag color="blue">{label}</Tag>;
        return <Tag>{label}</Tag>;
      },
    },
    {
      title: 'Approval',
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 120,
      filteredValue: undefined,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          Pending: 'default',
          Approved: 'success',
          Rejected: 'error'
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 240,
      filteredValue: undefined,
      render: (_: any, record: Quotation) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record.id)}
          >
            View
          </Button>
          <Tooltip title={canEditQuotation(record).ok ? undefined : canEditQuotation(record).reason}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/growth/quotations/edit/${record.id}`)}
              disabled={!canEditQuotation(record).ok}
            >
              Edit
            </Button>
          </Tooltip>
          {record.status === 'Sent' && (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleAcceptQuotation(record)}
              loading={acceptQuotationMutation.isPending}
              style={{ color: '#52c41a' }}
            >
              Accept
            </Button>
          )}
          {record.status === 'Accepted' && !record.is_converted_to_sales_order && (
            <Button
              type="link"
              size="small"
              icon={<ShoppingCartOutlined />}
              onClick={() => handleConvertToSalesOrder(record)}
              loading={convertToSalesOrderMutation.isPending}
            >
              Convert to SO
            </Button>
          )}
          <Button
            type="link"
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(record)}
          >
            Print
          </Button>
          <Popconfirm
            title="Delete Quotation"
            description={`Are you sure you want to delete quotation ${record.quotation_number}?`}
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
            disabled={!canDeleteQuotation(record).ok}
          >
            <Tooltip title={canDeleteQuotation(record).ok ? undefined : canDeleteQuotation(record).reason}>
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteQuotationMutation.isPending}
                disabled={!canDeleteQuotation(record).ok}
              >
                Delete
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div>
            <Title level={2}><FileTextOutlined /> Quotations</Title>
            <Text type="secondary">
              Manage customer quotations and proposals
            </Text>
            {opportunityIdFilter ? (
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                message={`Filtered by opportunity ${opportunityIdFilter}`}
                action={
                  <Button size="small" type="link" onClick={() => navigate('/growth/quotations')}>
                    Clear filter
                  </Button>
                }
              />
            ) : null}
          </div>

          {/* Statistics */}
          <Row gutter={16}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Quotations"
                  value={totalQuotations}
                  valueStyle={{ color: '#145A54' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Tooltip
                  title={`Converted to ${baseCurrency?.currency_code ?? 'base currency'} using master exchange rates`}
                >
                  <Statistic
                    title="Total Value"
                    value={currencyLoading ? 0 : totalValue}
                    precision={2}
                    prefix={
                      String(baseCurrency?.currency_code || '').toUpperCase() === AED_CURRENCY_CODE ? (
                        <AedDirhamSign />
                      ) : (
                        (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                      )
                    }
                    valueStyle={{ color: '#52c41a' }}
                    formatter={(val) =>
                      currencyLoading
                        ? '…'
                        : Number(val).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                    }
                  />
                </Tooltip>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Draft Quotations"
                  value={draftQuotations}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Quotations are created from estimations"
            description="Use BOQ, multi-item, or trading/rental estimation Convert to Quotation. Direct create and Excel import are disabled."
          />

          {/* Filters and Actions */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col flex="auto">
              <Space wrap>
                <Input
                  placeholder="Search number, name, customer, item code, description…"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  style={{ width: 360 }}
                />
                <Select
                  placeholder="Filter by status"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: 200 }}
                  allowClear
                >
                  <Option value="">All Status</Option>
                  <Option value="Draft">Draft</Option>
                  <Option value="Sent">Sent</Option>
                  <Option value="Accepted">Accepted</Option>
                  <Option value="Rejected">Rejected</Option>
                  <Option value="Revised">Revised</Option>
                  <Option value="AcceptedCurrent">Accepted (current offer)</Option>
                </Select>
                <Select
                  placeholder="Commercial outcome"
                  value={commercialStageFilter || undefined}
                  onChange={(v) => setCommercialStageFilter(v || '')}
                  style={{ width: 220 }}
                  allowClear
                >
                  <Option value="awaiting_customer_order">Awaiting customer order</Option>
                  <Option value="won_pr_awaiting_so">Won — PR booked</Option>
                  <Option value="converted_to_so">Converted to SO</Option>
                </Select>
                <Space>
                  <Switch checked={currentOfferOnly} onChange={setCurrentOfferOnly} size="small" />
                  <Text type="secondary">Current customer offer only</Text>
                </Space>
                <Select
                  placeholder="Sales team"
                  value={teamFilter || undefined}
                  onChange={(v) => setTeamFilter(v || '')}
                  style={{ width: 200 }}
                  allowClear
                  options={salesTeams.map((t) => ({ label: t.team_name, value: t.id }))}
                />
                <Space>
                  <Switch
                    checked={showAllRevisions}
                    onChange={setShowAllRevisions}
                    size="small"
                  />
                  <Text type="secondary">Show all revisions</Text>
                </Space>
                <Button icon={<DownloadOutlined />} onClick={downloadExport} disabled={isExcelBusy}>
                  Export
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={visibleQuotations}
            expandable={
              !showAllRevisions
                ? {
                    expandedRowRender: (record) => {
                      const family = expandedFamilies[record.id];
                      if (!family?.length) return <Text type="secondary">Loading family…</Text>;
                      return (
                        <Table
                          size="small"
                          pagination={false}
                          rowKey="id"
                          dataSource={family.filter((r) => r.id !== record.id)}
                          columns={[
                            {
                              title: 'Revision',
                              render: (_, r) => displayQuotationRevisionLabel(r.revision_number),
                            },
                            { title: 'Status', dataIndex: 'status' },
                            {
                              title: 'Role',
                              render: (_, r) =>
                                r.is_current_submitted ? (
                                  <Tag color="blue">Current</Tag>
                                ) : r.status === 'Revised' ? (
                                  <Tag>Superseded</Tag>
                                ) : (
                                  '—'
                                ),
                            },
                            {
                              title: 'Sell',
                              render: (_, r) =>
                                `${r.currency} ${Number(r.grand_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                            },
                            {
                              title: '',
                              render: (_, r) => (
                                <Button type="link" size="small" onClick={() => handleView(r.id)}>
                                  Open
                                </Button>
                              ),
                            },
                          ]}
                        />
                      );
                    },
                    onExpand: async (expanded, record) => {
                      if (!expanded || expandedFamilies[record.id]) return;
                      try {
                        const family = await quotationService.listRevisionFamily(record.id);
                        setExpandedFamilies((prev) => ({ ...prev, [record.id]: family }));
                      } catch {
                        setExpandedFamilies((prev) => ({ ...prev, [record.id]: [] }));
                      }
                    },
                    rowExpandable: (record) => record.is_latest_revision,
                  }
                : undefined
            }
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 'max-content' }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} quotations`
            }}
          />
        </Space>
      </Card>
    </div>
  );
};

export default QuotationsPage;
